import { useCallback, useEffect, useState } from 'react';
import { PlatformUnreachableError, type Participant } from '@sfc/sdk';
import { platform } from '../api/client';

export type ParticipantState =
  | { status: 'checking' }
  | { status: 'unreachable' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; participant: Participant };

/**
 * Who is signed in, asked of the server rather than guessed locally.
 *
 * Nothing is rendered from a cookie's existence. A revoked session would
 * otherwise flash a signed-in screen before disappearing, which reads as a bug
 * to the person it happens to and as a logout loop to the person debugging it.
 */
export function useParticipant(enabled: boolean): {
  state: ParticipantState;
  signedIn: (participant: Participant) => void;
  signOut: () => Promise<void>;
  recheck: () => void;
} {
  const [state, setState] = useState<ParticipantState>({ status: 'checking' });

  const check = useCallback(async (signal?: AbortSignal) => {
    try {
      const participant = await platform.match.current(signal);
      setState(participant ? { status: 'signed-in', participant } : { status: 'signed-out' });
    } catch (error) {
      if (signal?.aborted) return;
      // An unreachable API is a different screen from a signed-out one. Telling
      // somebody to sign in when the server is down sends them round a loop
      // that cannot end.
      setState(error instanceof PlatformUnreachableError ? { status: 'unreachable' } : { status: 'signed-out' });
    }
  }, []);

  useEffect(() => {
    /*
     * Not asked while a sign-in link is being spent.
     *
     * Both would be in flight at once, and the one that loses is the one whose
     * answer is stale: the check starts before the link is consumed, so it
     * truthfully reports nobody signed in, lands afterwards, and puts the sign-in
     * screen back up over a session that had just been created. Rather than
     * teaching the state to recognise its own stale answers, the question is not
     * asked while something else is already settling it.
     */
    if (!enabled) return;

    const controller = new AbortController();
    void check(controller.signal);
    return () => controller.abort();
  }, [check, enabled]);

  const signOut = useCallback(async () => {
    try {
      await platform.match.signOut();
    } finally {
      // Whatever the server said, this browser is done. A failed revocation is
      // the server's problem to sweep, not a reason to leave somebody looking
      // at a screen they asked to leave.
      setState({ status: 'signed-out' });
    }
  }, []);

  return {
    state,
    signedIn: (participant) => setState({ status: 'signed-in', participant }),
    signOut,
    recheck: () => void check(),
  };
}
